import { LoadPanel } from 'devextreme-react/load-panel';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../../../shared/AppContext';
import { CLASSES, checkPhasePermission } from '../../../../shared/Utils';
import { PermissionType } from '../../../../../dtos/PermissionType';
import PhaseHeader from '../../../../shared/tasklist/tasks/PhaseHeader';
import Tasks from '../../../../shared/tasklist/tasks/Tasks';
import { PhaseDto } from '../../../../../dtos/PhaseDto';
import { StepDto } from '../../../../../dtos/StepDto';
import { GrantDto } from '../../../../../dtos/GrantDto';
import { getPhaseById } from '../../../../../api/v2/PhasesApi';
import { TaskListProvider, useTaskListActions } from '../../../../shared/tasklist/context/TaskListContext';
import { parseDeepLinkHash } from '../../../../shared/tasklist/deepLinkUtils';

interface IGrantPhaseProps {
    currentGrantData: any, // faster initial load
    repopulateData: () => Promise<void>,
    // Reloads just the module's phase DTOs (dates). Deliberately separate from repopulateData,
    // which refetches grant data per phase and hits SharePoint.
    reloadPhaseDates?: () => Promise<void>,
    showStaticEntries: boolean,
    // The phase itself: its id, name, visibility, header (dates/download/comments) and tasklist
    // all come from here.
    phaseDto: PhaseDto,
    deepLinkHash?: string | null,
}

// Wrapper component to access TaskListContext and register refresh callback
interface ITasksWithRefreshProps {
    grantDto: GrantDto;
    selectedStep: StepDto | null;
    selectedPhase: PhaseDto | null;
    phaseInfo: { getData: () => Promise<PhaseDto | null> }[];
    repopulateData: () => Promise<void>;
    reloadPhaseDates?: () => Promise<void>;
    deepLinkHash: string | null;
    requestedSummaryPhase?: { phase: PhaseDto; token: number } | null;
    // Handed the tasklist's refresh so the phase header, which renders outside the provider, can
    // reload the subtask list after starting a resubmission.
    refreshRef: React.MutableRefObject<(() => Promise<void>) | null>;
    // The phase has the extended header, which shows its name.
    extendedHeader: boolean;
    // This module reloads every phase after an upload, because a rule can complete a task other
    // than the one the file landed in. Each task has its own tasklist context, so that refresh
    // never reaches the others - handing the reloaded phase in is what keeps their toolbars
    // current without a second round of fetching.
    phaseDto?: PhaseDto;
}

const TasksWithRefresh: React.FC<ITasksWithRefreshProps> = ({ grantDto, selectedStep, selectedPhase, phaseInfo, repopulateData, reloadPhaseDates, deepLinkHash, requestedSummaryPhase, refreshRef, extendedHeader, phaseDto }) => {
    const taskListActions = useTaskListActions();

    useEffect(() => {
        refreshRef.current = () => taskListActions.refreshSteps();
        return () => { refreshRef.current = null; };
    }, [taskListActions, refreshRef]);

    // Latest values for the refresh handler. Reading through refs lets us register
    // the handler once per mount instead of re-registering on every phase/step change,
    // which previously leaked handlers and caused N parallel refreshes (#941 flicker).
    const selectedPhaseRef = useRef(selectedPhase);
    const repopulateDataRef = useRef(repopulateData);
    const reloadPhaseDatesRef = useRef(reloadPhaseDates);
    useEffect(() => { selectedPhaseRef.current = selectedPhase; }, [selectedPhase]);
    useEffect(() => { repopulateDataRef.current = repopulateData; }, [repopulateData]);
    useEffect(() => { reloadPhaseDatesRef.current = reloadPhaseDates; }, [reloadPhaseDates]);

    // Register callback to refresh all DC phases when a subtask status changes
    useEffect(() => {
        const handleRefresh = async () => {
            await repopulateDataRef.current();

            // Upload-triggered derivation writes dates onto other phases in the module (a DD
            // document sets SD's completion), so every header can be stale - not just this one.
            await reloadPhaseDatesRef.current?.();

            // If a phase is currently selected, refetch it to get updated dates
            const currentPhase = selectedPhaseRef.current;
            if (currentPhase?.id) {
                try {
                    const refreshedPhase = await getPhaseById(currentPhase.id);
                    if (refreshedPhase?.data) {
                        taskListActions.setSelectedPhase(refreshedPhase.data);
                    }
                } catch (error) {
                    console.error('Failed to refresh selected phase:', error);
                }
            }
        };

        return taskListActions.registerForRefreshSteps(handleRefresh);
    }, [taskListActions]);

    return (
        <Tasks
            grantData={grantDto}
            selectedStep={selectedStep}
            selectedPhase={selectedPhase}
            phasesInfo={phaseInfo}
            deepLinkHash={deepLinkHash}
            requestedSummaryPhase={requestedSummaryPhase}
            // A phase with the extended header shows its name there, so the tasklist toolbar
            // must not repeat it - and must know that from the first render, before its own
            // copy of the phase has loaded.
            nameShownExternally={extendedHeader}
            externalPhase={phaseDto}
        />
    );
};

// new file to componentize each phase

export default function GrantPhase({ currentGrantData, repopulateData, reloadPhaseDates, showStaticEntries, phaseDto, deepLinkHash }: IGrantPhaseProps) {

    const app = useAppContext();

    // The tasklist mounts the first time the phase is opened and stays mounted after that;
    // isExpanded is whether its panel is showing. The header's status dot toggles it.
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const expand = useCallback(() => {
        setIsVisible(true);
        setIsExpanded(true);
    }, []);
    const toggleExpanded = useCallback(() => {
        setIsVisible(true);
        setIsExpanded(prev => !prev);
    }, []);

    // A task whose template turned the extended header off has no header row: its tasklist is
    // always open, and the tasklist toolbar carries the name, the status and its own collapse
    // toggle - the same as every other module.
    const extendedHeader = phaseDto?.defaultPhase?.settings?.extendedHeader === true;
    const headerless = phaseDto != null && !extendedHeader;
    useEffect(() => {
        if (headerless) expand();
    }, [headerless, expand]);

    // State for TaskListProvider
    const [selectedStep, setSelectedStep] = useState<StepDto | null>(null);
    const [selectedPhase, setSelectedPhase] = useState<PhaseDto | null>(null);

    // The mounted tasklist's refresh, or null while the panel has never been opened.
    const tasklistRefreshRef = useRef<(() => Promise<void>) | null>(null);

    // Set when the phase header's name is clicked. The token lets the same phase be
    // reopened; Tasks reads it and shows the summary (mounting first if the task was collapsed).
    const [requestedSummaryPhase, setRequestedSummaryPhase] = useState<{ phase: PhaseDto; token: number } | null>(null);

    // Global error handler for uncaught promise rejections from the tasklist
    useEffect(() => {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            if (event.reason === 'canceled' || event.reason?.message === 'canceled') {
                event.preventDefault(); // Prevent the error from being logged to console
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    }, []);

    // Convert currentGrantData to GrantDto format.
    // Memoize on the fields actually read so grantDto keeps a stable identity across
    // renders where the grant data is unchanged. Without this, a fresh object literal
    // every render breaks Tasks' React.memo and forces the subtask dataSource useMemo
    // to recreate and reload, doubling network calls on upload/comment (#941).
    const grantDto: GrantDto = useMemo(() => ({
        id: currentGrantData.id || '',
        fy: currentGrantData.fy || 0,
        grantNumber: currentGrantData.grantNumber || '',
        applicationId: 0,
        institution: currentGrantData.institution || '',
        shortInstitution: currentGrantData.shortInstitution || null,
        title: currentGrantData.title || '',
        shortTitle: currentGrantData.shortTitle || null,
        noa: currentGrantData.noa || null,
        budgetEndDate: currentGrantData.budgetEndDate || null,
        foa_nofo: currentGrantData.foA_NOFO || null,
        status: null,
        awarded: false,
        aor: currentGrantData.aor || null,
        aor_EmailAddress: currentGrantData.aorEmailAddress || null,
        pi: currentGrantData.pi || null,
        pi_EmailAddress: currentGrantData.pi_EmailAddress || null,
        piProfileId: currentGrantData.piProfileId || null,
        po: currentGrantData.po || null,
        po_EmailAddress: currentGrantData.po_EmailAddress || null,
        poProfileId: currentGrantData.poProfileId || null,
        gms: currentGrantData.gms || null,
        gms_EmailAddress: currentGrantData.gms_EmailAddress || null,
        gmsProfileId: currentGrantData.gmsProfileId || null,
        externalOrgId: null,
        spSyncDate: null,
        spId: null,
        spPath: null,
        soManuallyAssigned: null,
        modules: [],
    }), [
        currentGrantData.id, currentGrantData.fy, currentGrantData.grantNumber,
        currentGrantData.institution, currentGrantData.shortInstitution,
        currentGrantData.title, currentGrantData.shortTitle, currentGrantData.noa,
        currentGrantData.budgetEndDate, currentGrantData.foA_NOFO,
        currentGrantData.aor, currentGrantData.aorEmailAddress, currentGrantData.pi,
        currentGrantData.pi_EmailAddress, currentGrantData.piProfileId, currentGrantData.po,
        currentGrantData.po_EmailAddress, currentGrantData.poProfileId, currentGrantData.gms,
        currentGrantData.gms_EmailAddress, currentGrantData.gmsProfileId,
    ]);

    // Adapter to create phase info array for Tasks component.
    // Keyed on the stable phase id, not the PhaseDto: getPhaseById always fetches fresh, and a new
    // getData on every reload recreates the subtask dataSource and loads it twice.
    const phaseId = phaseDto.id;
    const phaseInfo = useMemo(() => [{
        getData: async (): Promise<PhaseDto | null> => {
            if (!phaseId) {
                return null;
            }

            try {
                const result = await getPhaseById(phaseId);
                if (result?.error) {
                    app.errorToast(result.error.message);
                    return null;
                }
                return result?.data ?? null;
            } catch (error: any) {
                // Don't show error toast if it's an abort error (user cancelled)
                if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
                    app.errorToast(`Failed to load phase data: ${error?.message || 'Unknown error'}`);
                }
                return null;
            }
        }
    }], [phaseId, app]);

    // Auto-expand phase if deep link hash targets this phase
    // The deep link format is #{module}-task-{phaseId}[-subtask-{stepId}],
    // so taskId in the parsed hash IS the phaseId. Direct GUID comparison, no API calls needed.
    useEffect(() => {
        if (!deepLinkHash || !phaseId) return;

        const { taskId } = parseDeepLinkHash(deepLinkHash);
        if (!taskId || taskId !== phaseId) return;

        // SubTaskList opens the summary itself for task-only links; this only opens the phase.
        expand();

        // Same-page deep links (e.g. clicking a Progress Monitor event tooltip) must move the
        // viewport to the targeted phase, not just expand it - expanding alone leaves the user
        // wherever they were scrolled. block:"start" anchors the phase header near the top. Scroll
        // even when the phase was already expanded, so re-clicking the same link still navigates.
        // Small delay to ensure the DOM is ready.
        setTimeout(() => {
            document.getElementById(phaseAnchorName() ?? "")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    }, [deepLinkHash, phaseId, expand])

    // The header's name click. Expands the task first if it's collapsed, then asks Tasks for the
    // summary - it mounts with the request pending and opens it.
    const onShowSummary = useCallback(() => {
        if (!phaseDto) return;

        expand();
        setRequestedSummaryPhase(prev => ({ phase: phaseDto, token: (prev?.token ?? 0) + 1 }));
    }, [phaseDto, expand]);

    // After a resubmission starts from the header icon. The mounted tasklist's refresh handler
    // already reloads the module's phases and headers along with the subtasks; before the panel
    // has ever opened there is no tasklist, so only the headers need reloading.
    const onCycleStarted = useCallback(async () => {
        if (tasklistRefreshRef.current) {
            await tasklistRefreshRef.current();
        } else {
            await reloadPhaseDates?.();
        }
    }, [reloadPhaseDates]);

    // This phase is "selected" when the tasklist has it open with no subtask drilled into, and the
    // panel is expanded. The provider mirrors its selection back into these state vars, so the header
    // - which renders outside the provider - can reflect it without reaching into the context. Gating
    // on isExpanded drops the shading once the phase is collapsed.
    const isPhaseSelected = isExpanded && !!selectedPhase && !selectedStep && !!phaseDto && selectedPhase.id === phaseDto.id;

    const phaseVisible = checkPhasePermission(phaseDto, app, PermissionType.View, true, currentGrantData.id);

    const phaseAnchorName = () => {
        return phaseDto.defaultPhase?.name?.toLowerCase().replace(" ", "-");
    }

    return (
        <>
            <div id={phaseAnchorName()} className={`row grantDetailsRowStepAccordion ${phaseVisible ? CLASSES.PHASESHOW : CLASSES.PHASEHIDE}`}>
                {/* The always-visible phase header (dates + comments) - shown collapsed or
                    expanded, so the dates are never lost. Self-contained, so it works outside the
                    tasklist. Absent while the PhaseDto is still loading, and for a task whose
                    template turned the extended header off - the tasklist toolbar carries the name
                    and status then, and no empty row is left where the header would have been. */}
                {extendedHeader && phaseDto && (
                    <div className="row" style={{ marginLeft: 4 }} >
                        <div className="dx-field col-12">
                            <PhaseHeader
                                phase={phaseDto}
                                grantData={currentGrantData}
                                showStaticEntries={showStaticEntries}
                                selected={isPhaseSelected}
                                onShowSummary={onShowSummary}
                                onChanged={repopulateData}
                                onCycleStarted={onCycleStarted}
                                expanded={isExpanded}
                                onToggleExpanded={toggleExpanded}
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="dx-field col-12" >
                        <div className="panel" style={{ display: isExpanded ? "block" : "none" }}>
                            {isVisible && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <TaskListProvider setSelectedStep={setSelectedStep} setSelectedPhase={setSelectedPhase}>
                                        <TasksWithRefresh
                                            grantDto={grantDto}
                                            selectedStep={selectedStep}
                                            selectedPhase={selectedPhase}
                                            phaseInfo={phaseInfo}
                                            repopulateData={repopulateData}
                                            reloadPhaseDates={reloadPhaseDates}
                                            deepLinkHash={deepLinkHash ?? null}
                                            requestedSummaryPhase={requestedSummaryPhase}
                                            refreshRef={tasklistRefreshRef}
                                            extendedHeader={extendedHeader}
                                            phaseDto={phaseDto}
                                        />
                                    </TaskListProvider>
                                </Suspense>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
